import { useSelector } from "@tanstack/react-store";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Stack, useTheme } from "@vapor/react-material";
import type { ReactElement } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetKPIs } from "../../../../../core/api/use-get-kpis/useGetKPIs";
import { useGetTaxSubmissions } from "../../../../../core/api/use-get-tax-submissions/useGetTaxSubmissions";
import {
    TaxSubmissionStatus,
    WARNING_SENT_STATUSES,
} from "../../../../../core/dtos/TaxSubmissionStatus";
import { store } from "../../../../../store/store";
import { taxSubmissionsStoreSlice } from "../../../../../store/taxSubmissionSearchFiltersStoreSlice";
import { TaxSubmissionGenericErrorKPICard } from "../tax-submission-generic-error-kpi-card/TaxSubmissionGenericErrorKPICard";
import { TaxSubmissionsTableHeaderKPICard } from "../tax-submissions-table-header-kpi-card/TaxSubmissionsTableHeaderKPICard";

interface TaxSubmissionsTableHeaderKPIsProps {
    accordionRight?: JSX.Element;
}

const SENT_STATUSES = [TaxSubmissionStatus.SENT, ...WARNING_SENT_STATUSES];

export const TaxSubmissionsTableHeaderKPIs = ({
    accordionRight,
}: TaxSubmissionsTableHeaderKPIsProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { data: taxSubmissions, isFetching: isFetchingTaxSubmissions } =
        useGetTaxSubmissions();
    const { data: kpisData, isFetching: isFetchingKpis } = useGetKPIs();
    const isRefreshing = isFetchingTaxSubmissions || isFetchingKpis;
    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const handleClickKPI = useCallback(
        (status: TaxSubmissionStatus) => {
            // if the clicked KPI is SENT, we need to consider both SENT, SENT_WITH_WARNINGS and OUTCOME_DOWNLOAD_ERROR statuses
            const statuses =
                status === TaxSubmissionStatus.SENT ? SENT_STATUSES : [status];

            const isKpiAlreadySelected = statuses.some((s) =>
                filters.statuses.includes(s),
            );

            const newSelectedKPIs = isKpiAlreadySelected
                ? filters.statuses.filter((kpi) => !statuses.includes(kpi))
                : Array.from(new Set([...filters.statuses, ...statuses]));

            taxSubmissionsStoreSlice.updateValues({
                statuses: newSelectedKPIs,
            });
        },
        [filters.statuses],
    );

    const genericErrorsCount = useMemo(
        () =>
            kpisData.find(
                (kpi) => kpi.status === TaxSubmissionStatus.GENERIC_ERROR,
            )?.numberOfTaxSubmissions || 0,
        [kpisData],
    );

    return (
        <>
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={2}>
                    <LoadingWrapper
                        loading={isRefreshing}
                        spinnerProps={{ loadingText: "" }}
                        variant="spinner"
                    >
                        <Typography
                            color={theme.palette.primary.textTitleColor}
                            variant="h5"
                        >
                            {taxSubmissions.totalCount}
                        </Typography>
                    </LoadingWrapper>
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        ml={1}
                        variant="h6"
                    >
                        {t(
                            "views.home.components.tax_submissions_table_header.kpis.accordion",
                        )}
                    </Typography>
                </Stack>
                {accordionRight}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="end">
                {genericErrorsCount > 0 && (
                    <TaxSubmissionGenericErrorKPICard
                        isSelected={
                            filters.statuses.indexOf(
                                TaxSubmissionStatus.GENERIC_ERROR,
                            ) > -1
                        }
                        loading={isRefreshing}
                        numberOfTaxSubmissions={genericErrorsCount}
                        onClickKPI={(status) => handleClickKPI(status)}
                    />
                )}
                {[
                    TaxSubmissionStatus.NOT_SENT_WITH_ERRORS,
                    TaxSubmissionStatus.REJECTED,
                    TaxSubmissionStatus.READY_TO_SEND,
                    TaxSubmissionStatus.PENDING,
                    TaxSubmissionStatus.SENT,
                ].map((status) => {
                    let taxSubmissionCount = 0;
                    let sentWithWarningsCount = 0;

                    if (status === TaxSubmissionStatus.SENT) {
                        kpisData.forEach((kpi) => {
                            if (SENT_STATUSES.includes(kpi.status))
                                taxSubmissionCount +=
                                    kpi.numberOfTaxSubmissions;

                            if (WARNING_SENT_STATUSES.includes(kpi.status))
                                sentWithWarningsCount +=
                                    kpi.numberOfTaxSubmissions;
                        });
                    } else {
                        taxSubmissionCount =
                            kpisData.find((kpi) => kpi.status === status)
                                ?.numberOfTaxSubmissions || 0;
                    }

                    const isSelected =
                        filters.statuses.indexOf(status) > -1 ||
                        (status === TaxSubmissionStatus.SENT &&
                            WARNING_SENT_STATUSES.some((s) =>
                                filters.statuses.includes(s),
                            ));

                    return (
                        <TaxSubmissionsTableHeaderKPICard
                            taxSubmissionStatus={status}
                            isSelected={isSelected}
                            loading={isRefreshing}
                            key={status}
                            numberOfTaxSubmissions={taxSubmissionCount}
                            numberOfWarnings={sentWithWarningsCount}
                            onClickKPI={(status) => handleClickKPI(status)}
                        />
                    );
                })}
            </Stack>
        </>
    );
};
